import React, { createRef, useEffect, RefObject } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { CustomInput, ToggleButton, UseFormDropDown } from '../../../components/common';
import Modal from '../../../components/common/modal';
import AlertBox from '../../../components/route-leaving-prompt/alert-box';
// TODO: Migrate to useAddResourceV3 / useUpdateResourceV3 when deprecating v2 resource endpoints (follow-up ticket).
import { useUpdateResource, useAddResource, useRemoveResource, Resource } from '../../../hooks/useResources';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { decimalOnChange, numberonBlur, onBlur } from '../../../components/common/useForm';
import { use2026TagGroups } from '../../../hooks/useTagGroups';
import { ResourceTagsEditModal } from '../../resources/resource-tags-modal';
import RichTextEditor from '../../../components/rich-text-editor/rich-text-editor';

type props = {
  resource: Resource;
  clientId: number;
};

const EditClientResourceDetails: React.FunctionComponent<props> = ({ resource, clientId }): JSX.Element => {
  const { updateResource } = useUpdateResource(resource.id!);
  const { addResource } = useAddResource();
  const { removeResource } = useRemoveResource(resource.id!);
  const [modal, setModal] = useState<boolean>(false);
  const [alertModal, setAlertModal] = useState<boolean>(false);
  const [enableSaveOption, setEnableSaveOption] = useState(false);
  const [errorMessages, setErrorMessages] = useState<any>({});
  const [imageBase64, setImageBase64] = useState<string>();
  const [resourceLogoWide, setResourceLogoWide] = useState<string>();
  const [showTagsModal, setShowTagsModal] = useState(false);

  const { allTags: tags } = use2026TagGroups();

  const fileInput: RefObject<HTMLInputElement> = createRef();
  const fileInputWide: React.RefObject<HTMLInputElement> = createRef();
  const defaultResource = {
    ...resource,
    description: resource?.description || '',
    long_description: resource?.long_description || '',
    meta_keywords: resource?.meta_keywords || '',
    image_url: resource?.image_url || null,
    wide_image_url: resource?.wide_image_url || null,
    name: resource?.name || '',
    phone_number: resource?.phone_number || '',
    sort_weight: resource?.sort_weight || 0,
    status: resource?.status || 'live',
    url: resource?.url || '',
    tag_ids: resource?.tag_ids || [],
    is_pre_crisis: resource?.is_pre_crisis || false,
  };

  const handleTagsChange = (tags: number[]) => {
    setValue('tag_ids', tags, { shouldDirty: true });
    setShowTagsModal(false);
  };

  const onFileChange = (event: React.FormEvent<HTMLInputElement>, wideImage?: boolean) => {
    try {
      if (event.currentTarget.files?.[0]) {
        let reader = new FileReader();

        reader.onload = (e: any) => {
          //Set the Base64 string return from FileReader as source.
          const image = new Image();

          if (wideImage) {
            setValue('wide_image_url', e.target.result);
            setResourceLogoWide(e.target.result);
            setEnableSaveOption(true);
          } else {
            //Validate the File Height and Width.
            image.onload = () => {
              // @ts-ignore
              const height = image.height;
              // @ts-ignore
              const width = image.width;
              if (height === width) {
                setValue('image_url', e.target.result);
                setImageBase64(e.target.result);
                setEnableSaveOption(true);
              } else {
                toast.error('Image must be equal height and width.');
                return new Error('Image must be equal height and width.');
              }
            };
          }
          image.src = e.target.result;
        };
        reader.readAsDataURL(event.currentTarget.files[0]);
      }
    } catch (error) {
      console.error('err--', error);
    }
  };

  const handleImageInput = (name: string) => {
    if (name === 'image_url') {
      fileInput.current?.click();
    }
    if (name === 'wide_image_url') {
      fileInputWide.current?.click();
    }
  };

  const setImageDisplay = useCallback(() => {
    if (resource?.image_url) {
      setImageBase64(resource.image_url);
    } else if (imageBase64 === '') {
      setImageBase64('');
    }

    if (resource?.wide_image_url) {
      setResourceLogoWide(resource.wide_image_url);
    } else if (resourceLogoWide === '') {
      setResourceLogoWide('');
    }
  }, [resource, imageBase64, resourceLogoWide]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: useMemo(() => {
      setImageDisplay();
      if (resource) {
        return defaultResource;
      }
    }, [resource]),
  });

  const validatePhone = async (data: any) => {
    let isValid = false;
    const hasAlphabeticCharacters = /[a-zA-Z]/.test(data.phone_number);
    // Remove non digits and validate that the number is either 0 digits (null), exactly 3 digits (N11 codes),
    // or between 10 and 15 digits (phone numbers ranging from US area code + number to maximum international length)
    const phoneNumber = data.phone_number.replace(/\D/g, '');
    const phoneNumberRegex = /^\d{3}$|^\d{10,15}$/;
    if (hasAlphabeticCharacters) {
      setErrorMessages({ phone_number: 'Phone number cannot contain alphabetic characters' });
    } else if (phoneNumber.length > 0 && !phoneNumberRegex.test(phoneNumber)) {
      setErrorMessages({ phone_number: 'Please enter a valid 3 or 10-15 digit phone number' });
    } else {
      setErrorMessages({});
      isValid = true;
    }

    return isValid;
  };

  useEffect(() => {
    if (resource.id) {
      reset(defaultResource);
      setImageBase64(resource?.image_url || '');
      setResourceLogoWide(resource?.wide_image_url || '');
      setEnableSaveOption(false);
    }
  }, [resource]);

  const handleSave = async (data: any) => {
    const payload = {
      ...data,
      phone_number: data?.phone_number ? data?.phone_number.replace(/[^\d+]/g, '') : null,
      image_url: data?.image_url ? (enableSaveOption ? data?.image_url : undefined) : null,
      wide_image_url: data?.wide_image_url ? (enableSaveOption ? data?.wide_image_url : undefined) : null,
      client_id: clientId,
    };

    if ((await validatePhone(data)) && resource?.id !== undefined) {
      updateResource.mutate(payload, {
        onSuccess: () => {
          toast.success('Resource updated successfully');
        },
      });
      setModal(false);
      setAlertModal(false);
      setEnableSaveOption(false);
      imageBase64 && setImageBase64('');
      resourceLogoWide && setResourceLogoWide('');
    } else if ((await validatePhone(data)) && resource?.id === undefined) {
      addResource.mutate(payload, {
        onSuccess: () => {
          toast.success('Resource added successfully');
        },
      });
      setModal(false);
      setAlertModal(false);
      setEnableSaveOption(false);
      reset();
      imageBase64 && setImageBase64('');
      resourceLogoWide && setResourceLogoWide('');
    }
  };

  const handleRemoveResource = async () => {
    removeResource.mutate();
    setModal(false);
    setAlertModal(false);
    setEnableSaveOption(false);
    reset();
  };

  return (
    <>
      <button
        onClick={(event) => {
          event.stopPropagation();
          setModal(true);
        }}
        className="bg-blue-primary rounded-sm px-2 py-2 text-center text-sm text-white"
      >
        {resource.id ? 'Edit' : 'Add Resource +'}
      </button>
      <Modal
        isModel={modal}
        InnerComponent={
          <div
            onClick={(event) => {
              event.stopPropagation();
            }}
            className="inline-block transform overflow-hidden bg-white align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:align-middle"
          >
            <form onSubmit={handleSubmit(handleSave)} id="resource-form">
              <div className="bg-white px-2 pb-2 pt-3 sm:p-6 sm:pb-4">
                <div className="flex justify-start">
                  <div>
                    <h3 className="blue-primary py-1 text-2xl font-normal">
                      {resource.id ? 'Edit Resource' : 'Add Resource'}
                    </h3>
                  </div>
                </div>
                <div className="flex-start flex py-2">
                  <h3 className="text-gray-dark py-1 text-lg font-bold">Details</h3>
                </div>
                <div className="mr-5 grid grid-cols-2 gap-3 gap-y-5">
                  <div>
                    <CustomInput
                      inputType="text"
                      Label="Resource Name"
                      register={{
                        ...register('name', {
                          required: 'Please provide resource name',
                          onBlur: (e: any) => onBlur(e, 'name', setValue),
                        }),
                      }}
                      //@ts-ignore
                      Error={!!errors?.name?.message?.length}
                      ErrorMessage={errors.name?.message}
                      dataTestId="resource-name"
                    />
                  </div>
                  <div className="text-left">
                    <UseFormDropDown label={'Status'}>
                      <select
                        className="select-box w-full focus:ring-transparent"
                        defaultValue="published"
                        {...register('status')}
                      >
                        <option value="live">Live</option>
                        <option value="hidden">Hidden</option>
                      </select>
                    </UseFormDropDown>
                  </div>
                  <div>
                    <h3 className="text-blue-dark mb-2 block text-left leading-4">Description</h3>
                    <div className="my-2 w-full">
                      <input
                        type="hidden"
                        {...register('description', {
                          required: 'Please provide resource description',
                        })}
                      />
                      <RichTextEditor
                        content={watch('description') || ''}
                        onChange={(value) =>
                          setValue('description', value, { shouldDirty: true, shouldValidate: true })
                        }
                        key={`client-resource-description-${resource.id ?? 'new'}`}
                      />
                      {
                        //@ts-ignore
                        !!errors?.description?.message?.length > 0 && (
                          <label className="text-red mb-2 block pt-2 text-xs font-medium leading-4">
                            {/* @ts-ignore */}
                            {errors.description?.message}
                          </label>
                        )
                      }
                    </div>
                  </div>
                  <div>
                    <h3 className="text-blue-dark mb-2 block text-left leading-4">Long Description</h3>
                    <div className="my-2 w-full">
                      <input type="hidden" {...register('long_description')} />
                      <RichTextEditor
                        content={watch('long_description') || ''}
                        onChange={(value) => setValue('long_description', value, { shouldDirty: true })}
                        key={`client-resource-long-description-${resource.id ?? 'new'}`}
                      />
                      {errors?.long_description?.message && errors.long_description.message.length > 0 && (
                        <label className="text-red mb-2 block pt-2 text-xs font-medium leading-4">
                          {/* @ts-ignore */}
                          {errors.long_description?.message}
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <h3 className="text-blue-dark mb-2 block text-left leading-4">Meta Keywords</h3>
                    <div className="my-2 w-full">
                      <textarea
                        className="text-gray-dark bg-gray-background-light h-28 w-full rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:outline-none"
                        {...register('meta_keywords', {
                          onBlur: (e: any) => onBlur(e, 'meta_keywords', setValue),
                        })}
                      />
                      {errors?.meta_keywords?.message && errors.meta_keywords.message.length > 0 && (
                        <label className="text-red mb-2 block pt-2 text-xs font-medium leading-4">
                          {/* @ts-ignore */}
                          {errors.meta_keywords?.message}
                        </label>
                      )}
                    </div>
                  </div>
                  <div>
                    <CustomInput
                      inputType="tel"
                      Label="Phone"
                      register={{
                        ...register('phone_number', {
                          onBlur: (e: any) => onBlur(e, 'phone_number', setValue),
                          onChange: (e) => {
                            validatePhone({ phone_number: e.target.value });
                          },
                        }),
                      }}
                      //@ts-ignore
                      Error={errorMessages.phone_number?.length > 0 ? true : false}
                      ErrorMessage={errorMessages.phone_number}
                      dataTestId="resource-phone"
                    />
                  </div>
                  <div>
                    <CustomInput
                      inputType="text"
                      Label="Link"
                      register={{
                        ...register('url', {
                          required: 'Please provide resource link',
                          onBlur: (e: any) => onBlur(e, 'url', setValue),
                        }),
                      }}
                      //@ts-ignore
                      Error={!!errors?.url?.message?.length}
                      ErrorMessage={errors.url?.message}
                      dataTestId="resource-link"
                    />
                  </div>
                  <div className="text-left">
                    <p>Resource Image</p>
                    <p className="py-2 pt-1 text-sm">Displays in app as benefit provider</p>
                    <div className="w-full">
                      <div
                        className={`aspect-ratio-container flex w-full items-center justify-center rounded-sm bg-gray-200 ${
                          !imageBase64 && 'cursor-pointer'
                        }`}
                        {...(!imageBase64 && { onClick: handleImageInput.bind(this, 'image_url') })}
                      >
                        {imageBase64 ? (
                          <img className="border" src={imageBase64} alt="clientImage" />
                        ) : (
                          <div className="flex h-80 items-center justify-center">
                            <button
                              className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl"
                              onClick={(ev) => {
                                ev.preventDefault();
                              }}
                            >
                              +
                            </button>
                            <input
                              ref={fileInput}
                              id="file-upload"
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onClick={() => {
                                if (fileInput.current) {
                                  fileInput.current.value = '';
                                }
                              }}
                              onChange={(data) => onFileChange(data)}
                            />
                          </div>
                        )}
                      </div>
                      <div className="px-12">
                        {imageBase64 && (
                          <button
                            className="bg-blue-primary my-2 w-full rounded-full p-2 text-center text-sm text-white"
                            onClick={() => {
                              setImageBase64('');
                              setValue('image_url', '');
                              setEnableSaveOption(true);
                            }}
                          >
                            REMOVE
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <CustomInput
                      inputType="text"
                      Label="Sort Order"
                      register={{
                        ...register('sort_weight', {
                          onChange: (e) => decimalOnChange(e, 'sort_weight', setValue, 2),
                          onBlur: (e) => numberonBlur(e, 'sort_weight', setValue),
                          valueAsNumber: true,
                        }),
                      }}
                      //@ts-ignore
                      Error={!!errors?.sort_weight?.message?.length}
                      ErrorMessage={errors.sort_weight?.message}
                      dataTestId="resource-sort-order"
                    />
                    <button
                      className="bg-blue-primary my-2 mt-8 w-full rounded-full p-2 text-center text-sm text-white"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowTagsModal(true);
                      }}
                    >
                      TAGS
                    </button>
                    <div className="grid grid-flow-row grid-cols-2 gap-x-4 gap-y-1 py-4">
                      {getValues('tag_ids').map((id) => {
                        return (
                          <div className="p-0.25 font-manrope block w-12 whitespace-nowrap text-sm font-medium leading-5 text-gray-800">
                            <div className="bg-blue-primary mr-2 inline-block h-2 w-2 rounded"></div>
                            {tags?.find((x) => x.id === id)?.name}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center py-4">
                      <span className="px-3">Nearly Crisis</span>{' '}
                      <ToggleButton
                        id={'is_pre_crisis'}
                        status={watch('is_pre_crisis')}
                        initialstatus={false}
                        setStatus={(data: boolean) => {
                          setValue('is_pre_crisis', data);
                          setEnableSaveOption(true);
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-span-2 text-left">
                    <p className="text-gray-dark pt-10 font-normal">Wide Resource Image</p>
                    <p className="text-gray-dark py-2 pt-1 font-normal">
                      Displays when peers are associated to resources
                    </p>
                    <p className="text-red py-2 pt-1 text-sm font-normal">
                      Wide resource images will look best with a 16:9 aspect ratio, a minimum resolution of 1280 x 720,
                      and as a transparent .png.
                    </p>
                    <div className="flex w-full">
                      <div
                        className={`${
                          !resourceLogoWide && 'cursor-pointer'
                        } bg-gray-background-light aspect-ratio-container w-full rounded-sm`}
                        {...(!resourceLogoWide && { onClick: handleImageInput.bind(this, 'wide_image_url') })}
                      >
                        {resourceLogoWide ? (
                          <div className="aspect-ratio-div">
                            <img className="aspect-ratio-container-img" src={resourceLogoWide} alt="resourceLogoWide" />
                          </div>
                        ) : (
                          <div className="flex h-56 items-center justify-center">
                            <button
                              className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl"
                              onClick={(ev) => {
                                ev.preventDefault();
                              }}
                            >
                              +
                            </button>
                            <input
                              ref={fileInputWide}
                              id="file-upload"
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onClick={() => {
                                if (fileInputWide.current) {
                                  fileInputWide.current.value = '';
                                }
                              }}
                              onChange={(data) => onFileChange(data, true)}
                            />
                          </div>
                        )}
                      </div>
                      <div className="pl-4">
                        <p className="text-gray-dark pt-4 font-normal">1280 x 720 px</p>
                        <div className="aspect-ratio-container w-full pt-2">
                          {resourceLogoWide && (
                            <div className="aspect-ratio-container w-full">
                              <button
                                className="bg-blue-primary my-2 w-full rounded-full p-2 text-center text-sm text-white"
                                onClick={() => {
                                  setResourceLogoWide('');
                                  setValue('wide_image_url', '');
                                  setEnableSaveOption(true);
                                }}
                              >
                                REMOVE
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    className={
                      'h-8 w-16 rounded-sm px-2 text-center text-sm text-white ' +
                      (isDirty || enableSaveOption ? 'bg-blue-primary' : 'bg-gray-200')
                    }
                    type="submit"
                    form="resource-form"
                    disabled={!isDirty && !enableSaveOption}
                  >
                    Save
                  </button>
                  <button
                    className="bg-blue-primary ml-4 h-8 w-16 rounded-sm px-2 text-center text-sm text-white"
                    type="button"
                    onClick={() => {
                      if (isDirty || enableSaveOption) {
                        setAlertModal(true);
                      } else {
                        setModal(false);
                      }
                    }}
                  >
                    Cancel
                  </button>
                  {resource.id && (
                    <button
                      className="bg-blue-primary ml-4 h-8 rounded-sm px-2 text-center text-sm text-white"
                      type="button"
                      onClick={handleRemoveResource}
                    >
                      Remove Resource
                    </button>
                  )}
                </div>
              </div>
            </form>
            {tags && showTagsModal && (
              <ResourceTagsEditModal
                onChange={handleTagsChange}
                onExit={() => setShowTagsModal(false)}
                selectedTagIds={getValues('tag_ids')}
                tags={tags}
              />
            )}
          </div>
        }
      />
      <AlertBox
        visible={alertModal}
        handlePrompt={() => {
          reset(defaultResource);
          setImageBase64('');
          setImageDisplay();
          setModal(false);
          setAlertModal(false);
          setEnableSaveOption(false);
        }}
        closeModal={() => {
          setAlertModal(false);
        }}
        confirmButtonText={'Discard Changes'}
        titleText={'Alert'}
        contentText={'You have unsaved changes. If you leave this screen without saving, your changes will be lost.'}
        cancelButtonText={'Cancel'}
      />
    </>
  );
};

export default EditClientResourceDetails;
